/**
 * The blind receipt and what it posts (Phase 2 W5-3; docs/plans/PHASE_2_W5.md §5; owner points 3
 * and 5). One pure function, shared by the API (which posts) and the tests; the database checks the
 * same arithmetic again (0030).
 *
 * Per invoice line, with good = received − damaged:
 * - accepted = min(good, invoiced): it enters stock;
 * - claim = invoiced − accepted: short or damaged, owed back by the supplier (1360);
 * - excess = good − invoiced when above 0: held aside, waiting for the warehouse manager;
 * - accepted value = round_half_up(net line value × accepted ÷ invoiced) — exactly the line when all
 *   was accepted — and claim value = net line value − accepted value, so they always add up.
 * A line received in several batches is accepted from the earliest expiry first (the latest-expiry
 * goods are the excess: FEFO keeps the earliest in stock), and its accepted value is split over its
 * batches by largest remainder. Goods that are not on the invoice are all excess.
 */
import { z } from 'zod';

import { PhotoHandoffRefSchema } from '../attachments/handoff.ts';
import { isUuidV7 } from '../ids/ids.ts';
import { allocate, divideRounded, iqd } from '../money/money.ts';

export interface ReceiptInvoiceLine {
  readonly lineNo: number;
  readonly itemId: string;
  /** Invoiced quantity in stock units. */
  readonly quantity: number;
  /** The line's value after the invoice discount (its share of what is owed). */
  readonly netTotal: number;
}

export interface ReceiptRowInput {
  readonly rowNo: number;
  /** Null: an item that arrived but is not on the invoice. */
  readonly invoiceLineNo: number | null;
  readonly itemId: string;
  /** Batch items only; the earliest expiry is accepted first. */
  readonly expiryDate: string | null;
  readonly received: number;
  readonly damaged: number;
}

export interface ReceiptLineResult {
  readonly lineNo: number;
  readonly itemId: string;
  readonly invoiced: number;
  readonly received: number;
  readonly damaged: number;
  readonly accepted: number;
  readonly claimQuantity: number;
  readonly excess: number;
  readonly lineValue: number;
  readonly acceptedValue: number;
  readonly claimValue: number;
}

export interface ReceiptRowResult {
  readonly rowNo: number;
  readonly accepted: number;
  readonly excess: number;
  readonly acceptedValue: number;
}

export interface ReceiptPosting {
  readonly lines: readonly ReceiptLineResult[];
  readonly rows: readonly ReceiptRowResult[];
  readonly acceptedValue: number;
  readonly claimValue: number;
  /** What the supplier is owed for this invoice: accepted + claims = the invoice total. */
  readonly invoiceTotal: number;
}

export type ReceiptProblem =
  | { readonly kind: 'line_missing'; readonly lineNo: number }
  | { readonly kind: 'unknown_line'; readonly rowNo: number }
  | { readonly kind: 'other_item'; readonly rowNo: number }
  | { readonly kind: 'damaged_more_than_received'; readonly rowNo: number }
  | { readonly kind: 'negative'; readonly rowNo: number };

/** Checks the counted rows against the invoice lines (every line answered, even with 0). */
export function receiptProblems(
  lines: readonly ReceiptInvoiceLine[],
  rows: readonly ReceiptRowInput[],
): ReceiptProblem[] {
  const problems: ReceiptProblem[] = [];
  const byNo = new Map(lines.map((l) => [l.lineNo, l]));
  for (const row of rows) {
    if (
      !Number.isSafeInteger(row.received) ||
      !Number.isSafeInteger(row.damaged) ||
      row.received < 0 ||
      row.damaged < 0
    ) {
      problems.push({ kind: 'negative', rowNo: row.rowNo });
    } else if (row.damaged > row.received) {
      problems.push({ kind: 'damaged_more_than_received', rowNo: row.rowNo });
    }
    if (row.invoiceLineNo !== null) {
      const line = byNo.get(row.invoiceLineNo);
      if (!line) problems.push({ kind: 'unknown_line', rowNo: row.rowNo });
      else if (line.itemId !== row.itemId) problems.push({ kind: 'other_item', rowNo: row.rowNo });
    }
  }
  for (const line of lines) {
    if (!rows.some((r) => r.invoiceLineNo === line.lineNo)) {
      problems.push({ kind: 'line_missing', lineNo: line.lineNo });
    }
  }
  return problems;
}

const byExpiry = (a: ReceiptRowInput, b: ReceiptRowInput) =>
  (a.expiryDate ?? '9999-12-31').localeCompare(b.expiryDate ?? '9999-12-31') || a.rowNo - b.rowNo;

/** What a receipt posts (call `receiptProblems` first: this assumes the rows are valid). */
export function postReceipt(
  lines: readonly ReceiptInvoiceLine[],
  rows: readonly ReceiptRowInput[],
): ReceiptPosting {
  const rowResults = new Map<number, ReceiptRowResult>();
  const lineResults: ReceiptLineResult[] = [];
  for (const line of lines) {
    const mine = rows.filter((r) => r.invoiceLineNo === line.lineNo).sort(byExpiry);
    const received = mine.reduce((s, r) => s + r.received, 0);
    const damaged = mine.reduce((s, r) => s + r.damaged, 0);
    const good = received - damaged;
    const accepted = Math.min(good, line.quantity);
    const acceptedValue =
      accepted === line.quantity
        ? line.netTotal
        : Number(
            divideRounded(
              BigInt(line.netTotal) * BigInt(accepted),
              BigInt(line.quantity),
              'HALF_UP',
            ),
          );
    // Accepted from the earliest expiry first; the rest of each row's good goods is excess.
    let left = accepted;
    const takes = mine.map((r) => {
      const take = Math.min(r.received - r.damaged, left);
      left -= take;
      return take;
    });
    const values =
      acceptedValue > 0 && accepted > 0
        ? allocate(iqd(acceptedValue), takes, 'LARGEST_REMAINDER').shares
        : takes.map(() => 0);
    mine.forEach((r, i) => {
      const take = takes[i] ?? 0;
      rowResults.set(r.rowNo, {
        rowNo: r.rowNo,
        accepted: take,
        excess: r.received - r.damaged - take,
        acceptedValue: values[i] ?? 0,
      });
    });
    lineResults.push({
      lineNo: line.lineNo,
      itemId: line.itemId,
      invoiced: line.quantity,
      received,
      damaged,
      accepted,
      claimQuantity: line.quantity - accepted,
      excess: Math.max(0, good - line.quantity),
      lineValue: line.netTotal,
      acceptedValue,
      claimValue: line.netTotal - acceptedValue,
    });
  }
  // Goods not on the invoice: all excess, no value.
  for (const r of rows.filter((x) => x.invoiceLineNo === null)) {
    rowResults.set(r.rowNo, {
      rowNo: r.rowNo,
      accepted: 0,
      excess: r.received - r.damaged,
      acceptedValue: 0,
    });
  }
  const acceptedValue = lineResults.reduce((s, l) => s + l.acceptedValue, 0);
  const claimValue = lineResults.reduce((s, l) => s + l.claimValue, 0);
  return {
    lines: lineResults,
    rows: rows.map(
      (r) =>
        rowResults.get(r.rowNo) ?? { rowNo: r.rowNo, accepted: 0, excess: 0, acceptedValue: 0 },
    ),
    acceptedValue,
    claimValue,
    invoiceTotal: lines.reduce((s, l) => s + l.netTotal, 0),
  };
}

// ── What is sent ─────────────────────────────────────────────────────────────

const SAFE_QUANTITY = Number.MAX_SAFE_INTEGER;

/**
 * One counted row: an invoice line (or an item not on the invoice), one batch of it — what is
 * counted, wherever it is typed (online, a device's sync command, a PC's page; W5-4). Only the
 * damage photos differ: stored ids online, declarations in a command, bytes on a PC.
 */
export const receiptRowFields = {
  invoiceLineNo: z.number().int().min(1).nullable(),
  itemId: z.uuid(),
  /** In the item's stock unit; 0 is an answer ("nothing arrived"). */
  received: z.number().int().min(0).max(SAFE_QUANTITY),
  damaged: z.number().int().min(0).max(SAFE_QUANTITY).default(0),
  /** Batch items: the supplier's lot (empty: the server makes one) and the dates. */
  batchCode: z.string().trim().min(1).max(40).nullable().default(null),
  productionDate: z.iso.date().nullable().default(null),
  expiryDate: z.iso.date().nullable().default(null),
};
export const RECEIPT_ROWS_MAX = 500;
export const RECEIPT_ROW_PHOTOS_MAX = 10;

export const GoodsReceiptRowSchema = z.strictObject({
  ...receiptRowFields,
  /** DAMAGE photos of this row (at least one when something is damaged, or a hand-off's). */
  damagePhotoIds: z.array(z.uuid()).max(RECEIPT_ROW_PHOTOS_MAX).default([]),
  /** The damage photos a phone sent for this row through the QR code (W5-6). */
  damageHandoff: PhotoHandoffRefSchema.nullable().default(null),
});
export type GoodsReceiptRow = z.infer<typeof GoodsReceiptRowSchema>;

/**
 * Chosen by the device: the same receipt sent twice is recorded once. A UUIDv7 or refused
 * (CLAUDE.md §3.3; owner, 2026-10-09).
 */
export const receiptIdSchema = z
  .uuid()
  .refine(isUuidV7, { message: 'UUIDv7 expected', params: { dobCode: 'invalid_id' } });

export const GoodsReceiptSchema = z.strictObject({
  id: receiptIdSchema,
  invoiceId: z.uuid(),
  rows: z.array(GoodsReceiptRowSchema).min(1).max(RECEIPT_ROWS_MAX),
  note: z.string().trim().max(500).nullable().default(null),
});
export type GoodsReceipt = z.infer<typeof GoodsReceiptSchema>;
export type GoodsReceiptInput = z.input<typeof GoodsReceiptSchema>;

/** An excess row returned to the supplier: closed with a reason and a photo of the return paper. */
export const ExcessReturnSchema = z.strictObject({
  /** Chosen by the page: a retry with the same id is the same return. A UUIDv7 or refused. */
  id: z.uuid().refine(isUuidV7, { message: 'UUIDv7 expected', params: { dobCode: 'invalid_id' } }),
  receiptId: z.uuid(),
  rowNo: z.number().int().min(1),
  /** Null: all that is left of this excess. */
  quantity: z.number().int().min(1).max(SAFE_QUANTITY).nullable().default(null),
  reason: z.string().trim().min(10).max(500),
  attachmentIds: z.array(z.uuid()).min(1).max(10),
});
export type ExcessReturn = z.infer<typeof ExcessReturnSchema>;
